// Edit mode (docs/plans/admin.md section 2, Phase 2). There is no sign-in control on the site: the shared admin link
// is any room URL with `#edit=<key>` at the end. On start the key is read from the fragment (fragments are not sent
// to servers, so it is not in any request line or log), the fragment is removed from the address bar, and the key
// is exchanged once at POST /auth/link for a session token. The token is kept in localStorage (`lr3d.session`, 90
// days) and sent as X-Editor-Session on picture writes and in the WebSocket `auth` message, so the browser stays in
// edit mode at the normal room URLs.
//
// GET /auth/session at start tells which areas the server locks (EDIT_LOCK) and whether the token is still good.
// An area that is not locked is open to everyone, as before the admin: with the lock off nothing changes.

const KEY = 'lr3d.session';
const FRAGMENT_RE = /(?:^#|&)edit=([0-9a-fA-F]{32})(?:&|$)/;

export type EditArea = 'art' | 'vars' | 'overlay' | 'admin';

interface State {
  /** The editor's name ("link" for the admin link), or null. */
  editor: string | null;
  /** Areas the server locks. Empty until the server answered (and when it has no /auth): today's behaviour. */
  lock: Set<EditArea>;
}

const state: State = { editor: null, lock: new Set() };
const listeners = new Set<() => void>();

function readToken(): string | null {
  try { return localStorage.getItem(KEY); } catch { return null; }
}

function writeToken(t: string | null): void {
  try {
    if (t) localStorage.setItem(KEY, t);
    else localStorage.removeItem(KEY);
  } catch { /* storage blocked: edit mode lasts for this page only */ }
}

let token: string | null = readToken();

/** The session token to send (X-Editor-Session, WebSocket `auth`), or null. */
export function sessionToken(): string | null {
  return token;
}

/** Headers for a write request: X-Editor-Session when this browser has a session. */
export function editorHeaders(): Record<string, string> {
  return token ? { 'x-editor-session': token } : {};
}

/** May this browser change `area`? True when the area is not locked, or with an editor's session. */
export function mayEdit(area: EditArea): boolean {
  return !state.lock.has(area) || !!state.editor;
}

/** The area is locked and this browser has no editor's session (visitor). */
export function lockedOut(area: EditArea): boolean {
  return !mayEdit(area);
}

/** Called after the edit state changed (session checked, admin link exchanged, session ended). */
export function onEditChange(f: () => void): () => void {
  listeners.add(f);
  return () => listeners.delete(f);
}

function changed(): void {
  for (const f of listeners) f();
}

/** The admin link's key from the address bar's fragment, which is then removed (history.replaceState). */
function takeKey(): string | null {
  const m = FRAGMENT_RE.exec(location.hash);
  if (!m) return null;
  const rest = location.hash.replace(/^#/, '').split('&').filter((p) => !p.startsWith('edit=')).join('&');
  history.replaceState(history.state, '', location.pathname + location.search + (rest ? `#${rest}` : ''));
  return m[1]!.toLowerCase();
}

async function post(url: string, body: unknown): Promise<{ status: number; json: Record<string, unknown> }> {
  const r = await fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), cache: 'no-store' });
  let json: Record<string, unknown> = {};
  try { json = await r.json(); } catch { /* not JSON */ }
  return { status: r.status, json };
}

/**
 * Start: take the admin link's key from the URL (exchanged for a session), then ask the server for the locked areas
 * and the session's editor. `base`: the room server's HTTP origin (null: single-player, nothing to do). Returns a
 * line for the user after an admin link was opened (edit mode on, or why not), else null.
 */
export async function initEditMode(base: string | null): Promise<string | null> {
  const key = takeKey();
  if (!base) return key ? 'Edit mode needs the room server; it is not connected.' : null;
  let message: string | null = null;
  if (key) {
    try {
      const r = await post(`${base}/auth/link`, { key });
      if (r.status === 200 && typeof r.json.session === 'string') {
        token = r.json.session;
        writeToken(token);
        message = 'Edit mode is on in this browser for 90 days.';
      } else if (r.status === 429) message = 'Too many attempts. Wait a minute and open the admin link again.';
      else message = 'This admin link is not valid (a newer one may have replaced it).';
    } catch {
      message = 'The server could not be reached; open the admin link again later.';
    }
  }
  await checkSession(base);
  return message;
}

/** GET /auth/session: the locked areas and whether the stored token is still good (a bad one is deleted). */
export async function checkSession(base: string): Promise<void> {
  try {
    const r = await fetch(`${base}/auth/session`, { headers: editorHeaders(), cache: 'no-store' });
    if (r.status === 404) { state.lock = new Set(); changed(); return; } // a server without /auth: everything open
    const j = (await r.json()) as { ok?: boolean; name?: string; lock?: string[] };
    state.lock = new Set((Array.isArray(j.lock) ? j.lock : []) as EditArea[]);
    if (r.ok && j.ok && typeof j.name === 'string') state.editor = j.name;
    else {
      state.editor = null;
      if (r.status === 401 && token) { token = null; writeToken(null); }
    }
  } catch {
    // unreachable: keep what we had (nothing locked until the server says so)
  }
  changed();
}

/** Leave edit mode in this browser (the console: `__lr.leaveEditMode()`). */
export function leaveEditMode(): void {
  token = null;
  writeToken(null);
  state.editor = null;
  changed();
}
